import { notifyOrder } from '@/components/snackbar/order-snackbar';
import { TerminalPositionService } from '@/service/positions/terminal-position';
import type { SymbolSpecDto } from '@/service/positions/terminal-position-types';
import {
  MutationOptions,
  useMutation,
  useQueries,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import i18next from 'i18next';

const RECENT_ORDERS_KEY = ['TerminalPositionService.getRecentOrders'];

/// Le compte n'a pas fermé lui-même : un second terminal s'en charge.
const notifyRescue = () =>
  notifyOrder(
    'sent',
    i18next.t('metrix:actions.closeRescue'),
    i18next.t('metrix:actions.closeRescueHint'),
    // « Tout fermer » sur plusieurs positions : une seule carte.
    { autoHideDuration: 12_000, preventDuplicate: true },
  );

export const useTakePositionsMutation = (
  opt?: MutationOptions<
    Awaited<ReturnType<typeof TerminalPositionService.takePositions>>,
    Error,
    Parameters<typeof TerminalPositionService.takePositions>[0]
  >,
) => {
  return useMutation({
    ...opt,
    mutationFn: TerminalPositionService.takePositions,
    onSuccess(data, variables, context) {
      if (opt?.onSuccess) opt.onSuccess(data, variables, context);
    },
  });
};

/// Suit des ordres déposés jusqu'à ce que leur compte les montre. Tant que
/// l'un d'eux n'est ni visible ni refusé, on redemande toutes les 2 s.
export const useGetOrdersQuery = (
  ids: string[],
  opt?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<typeof TerminalPositionService.getOrders>>
    >
  >,
) =>
  useQuery({
    ...opt,
    queryFn: () => TerminalPositionService.getOrders(ids),
    queryKey: ['TerminalPositionService.getOrders', ids],
    enabled: ids.length > 0 && opt?.enabled !== false,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data?.length) return 2000;
      return data.some((o) => o.status !== 'rejected' && !o.onAccount)
        ? 2000
        : false;
    },
  });

export const useGetRecentOrdersQuery = (
  limit?: number,
  opt?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<typeof TerminalPositionService.getRecentOrders>>
    >
  >,
) =>
  useQuery({
    ...opt,
    queryFn: () => TerminalPositionService.getRecentOrders(limit),
    queryKey: [...RECENT_ORDERS_KEY, limit ?? null],
  });

/// Les spécifications du symbole chez chaque compte visé : un compte par
/// broker, donc un lot minimum et un pas par compte.
export const useGetSymbolSpecsQueries = (
  accountIds: string[],
  symbol: string | null,
) =>
  useQueries({
    queries: accountIds.map((accountId) => ({
      queryFn: () =>
        TerminalPositionService.getSymbolSpec({
          accountId,
          symbol: symbol as string,
        }),
      queryKey: ['TerminalPositionService.getSymbolSpec', accountId, symbol],
      enabled: !!symbol,
      // Les bornes d'un symbole ne bougent pas dans la séance.
      staleTime: 30 * 60_000,
      retry: false,
    })),
  });

const specData = (results: UseQueryResult<SymbolSpecDto>[]) =>
  results.map((r) => r.data);

/// Fiches symbole de plusieurs comptes, chacun sous le nom de son broker
/// (EURUSD, EURUSD.r) : dans l'ordre des paires, `undefined` tant qu'une fiche
/// n'est pas lue. La liste rendue ne change que si une fiche change.
export const useGetSymbolSpecsByAccountQueries = (
  pairs: { accountId: string; symbol: string }[],
) =>
  useQueries({
    queries: pairs.map(({ accountId, symbol }) => ({
      queryFn: () => TerminalPositionService.getSymbolSpec({ accountId, symbol }),
      queryKey: ['TerminalPositionService.getSymbolSpec', accountId, symbol],
      staleTime: 30 * 60_000,
      retry: false,
    })),
    combine: specData,
  });

export const useClosePositionMutation = (
  opt?: MutationOptions<
    Awaited<ReturnType<typeof TerminalPositionService.closePosition>>,
    Error,
    Parameters<typeof TerminalPositionService.closePosition>[0]
  >,
) => {
  return useMutation({
    ...opt,
    mutationFn: (close) =>
      TerminalPositionService.closePosition(close, notifyRescue),
  });
};

export const usePartialClosePositionMutation = (
  opt?: MutationOptions<
    Awaited<ReturnType<typeof TerminalPositionService.partialClosePosition>>,
    Error,
    Parameters<typeof TerminalPositionService.partialClosePosition>[0]
  >,
) => {
  return useMutation({
    ...opt,
    mutationFn: (close) =>
      TerminalPositionService.partialClosePosition(close, notifyRescue),
  });
};

export const useModifyPositionMutation = (
  opt?: MutationOptions<
    Awaited<ReturnType<typeof TerminalPositionService.modifyPosition>>,
    Error,
    Parameters<typeof TerminalPositionService.modifyPosition>[0]
  >,
) => {
  return useMutation({
    ...opt,
    mutationFn: TerminalPositionService.modifyPosition,
  });
};

export const useCancelOrderMutation = (
  opt?: MutationOptions<
    Awaited<ReturnType<typeof TerminalPositionService.cancelOrder>>,
    Error,
    Parameters<typeof TerminalPositionService.cancelOrder>[0]
  >,
) => {
  return useMutation({
    ...opt,
    mutationFn: TerminalPositionService.cancelOrder,
  });
};

export const useModifyOrderMutation = (
  opt?: MutationOptions<
    Awaited<ReturnType<typeof TerminalPositionService.modifyOrder>>,
    Error,
    Parameters<typeof TerminalPositionService.modifyOrder>[0]
  >,
) => {
  return useMutation({
    ...opt,
    mutationFn: TerminalPositionService.modifyOrder,
  });
};
